import React, { useState } from "react";
import axios from "axios";
import { Reveal, SectionLabel } from "../components/primitives";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: "lp", label: "I'm a prospective LP" },
  { id: "founder", label: "I'm a founder" },
  { id: "press", label: "Press / media" },
  { id: "general", label: "General inquiry" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", inquiry_type: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", organization: "", inquiry_type: "general", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-20 pb-12 lg:pt-32 lg:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SectionLabel>Contact</SectionLabel>
          <h1 className="font-display font-bold tracking-[-0.035em] leading-[0.94] text-5xl sm:text-7xl lg:text-[104px] text-[#111A15] max-w-5xl">
            Let's talk.
          </h1>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[17px] text-[#4A5E51] leading-relaxed">Whether you're a fund of funds, a family office considering an LP commitment, a climate-tech founder building in our 9 sectors, or media — we'd love to hear from you.</p>

            <div className="mt-12 space-y-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mb-2">— Email</p>
                <a href="mailto:info@folksfunds.com" className="font-display font-medium text-2xl text-[#111A15] hover:text-[#C44900] transition-colors break-all">info@folksfunds.com</a>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mb-2">— Office</p>
                <p className="font-display text-xl text-[#111A15]">New Delhi, India</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mb-2">— Regulatory</p>
                <p className="text-[14px] text-[#4A5E51]">SEBI-registered AIF Category II.<br />Investments restricted to qualified investors.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E5E5E0] p-8 lg:p-12">
              {done ? (
                <div data-testid="inquiry-success" className="flex items-start gap-5 py-6">
                  <CheckCircle2 className="w-7 h-7 text-[#1A2F24] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="font-display font-semibold text-2xl text-[#111A15] mb-2">Message received.</p>
                    <p className="text-[14px] text-[#4A5E51] leading-relaxed">Thank you. A member of the team will respond within two business days.</p>
                    <button onClick={() => setDone(false)} className="mt-6 text-[13px] font-mono uppercase tracking-[0.18em] text-[#C44900] hover:underline">Send another →</button>
                  </div>
                </div>
              ) : (
                <form data-testid="inquiry-form" onSubmit={onSubmit} className="space-y-7">
                  <div>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mb-3">— I am</span>
                    <div className="flex flex-wrap gap-2">
                      {TYPES.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          data-testid={`inquiry-type-${t.id}`}
                          onClick={() => setForm({ ...form, inquiry_type: t.id })}
                          className={`px-4 py-2 text-[12px] font-mono uppercase tracking-[0.14em] border transition-colors ${
                            form.inquiry_type === t.id
                              ? "bg-[#1A2F24] text-white border-[#1A2F24]"
                              : "bg-transparent text-[#4A5E51] border-[#E5E5E0] hover:border-[#1A2F24] hover:text-[#111A15]"
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                    <Field label="Name" required>
                      <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Email" required>
                      <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Organization" className="md:col-span-2">
                      <input data-testid="contact-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className={inputCls} />
                    </Field>
                    <Field label="Message" required className="md:col-span-2">
                      <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} placeholder="Tell us why you're reaching out." />
                    </Field>
                  </div>

                  <button data-testid="contact-submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#1A2F24] text-white px-7 py-3.5 text-[14px] font-medium hover:bg-[#111A15] transition-colors disabled:opacity-40">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    {submitting ? "Sending" : "Send message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-b-2 border-[#E5E5E0] py-3 text-[15px] text-[#111A15] focus:outline-none focus:border-[#1A2F24] transition-colors placeholder:text-[#9DB0A3]";

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#4A5E51] mb-2">— {label}{required && <span className="text-[#C44900]"> *</span>}</span>
      {children}
    </label>
  );
}
